import { type RefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type BasePuiDialogProps,
  DateFormat,
  type FileTemplate,
  type IFrameEvent,
  moment,
  type Nil,
  PuiDialog,
  Text,
  Utils,
} from '@pbt/pbt-ui-components'

import DocumentDialogStates from '~/constants/DocumentDialogStates'
import { matchImageExtension } from '~/constants/extensions'
import FeatureToggle from '~/constants/featureToggle'
import { createDocument } from '~/store/actions/documents'
import {
  clearDocumentValidationError,
  fetchSoap,
  updateDocumentOnSoap,
} from '~/store/actions/soap'
import { getDocumentTypes, getFeatureToggle } from '~/store/reducers/constants'
import {
  getDocumentsIsSending,
  getLastCreatedDocumentId,
} from '~/store/reducers/documents'
import {
  getDocumentValidationError,
  getIsAttachingDocuments,
  getSoapBusinessId,
  getSoapId,
} from '~/store/reducers/soap'
import type {
  BaseDataHandleWithUnsavedChanges,
  Document as DocumentType,
  SoapFile,
} from '~/types'
import { addSearch } from '~/utils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useErrorAlert from '~/utils/useErrorAlert'

import Document, { type DocumentHandle } from './Document'
import DocumentEditor, { type DocumentEditorHandle } from './DocumentEditor'
import DocumentEditorActions from './DocumentEditorActions'
import DocumentPreview from './DocumentPreview'
import DocumentPreviewActions from './DocumentPreviewActions'
import { createTemplateFromFile } from './documentUtils'

const safeGetHasChanges =
  (ref: RefObject<BaseDataHandleWithUnsavedChanges>) => () =>
    ref.current?.hasUnsavedChanges() || false

const useStyles = makeStyles(
  () => ({
    paper: {
      overflowY: 'visible',
      minWidth: 660,
      maxWidth: '100%',
    },
    itemsPaper: {
      width: 976,
      maxWidth: 976,
    },
  }),
  { name: 'DocumentDialog' },
)

interface DocumentDialogProps extends BasePuiDialogProps {
  PreviewProps?: any
  conversationId?: string
  disableCloseAfterUpdate?: boolean
  document?: DocumentType
  onDocumentCandidateReady?: (newDocument: DocumentType) => void
  previewOnly?: boolean
  step?: DocumentDialogStates
  view?: boolean
}

const DocumentDialog = ({
  previewOnly = false,
  PreviewProps = {},
  step: stepProp,
  document,
  open,
  view,
  onClose,
  conversationId,
  disableCloseAfterUpdate,
  onDocumentCandidateReady: onDocumentCandidateReadyProps,
}: DocumentDialogProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const lastCreatedDocumentId = useSelector(getLastCreatedDocumentId)
  const soapId = useSelector(getSoapId)
  const soapBusinessId = useSelector(getSoapBusinessId)
  const DocumentTypes = useSelector(getDocumentTypes)

  const { t } = useTranslation(['Common', 'Dialogs'])

  const [documentCandidate, setDocumentCandidate] = useState(
    document as DocumentType,
  )
  const [step, setStep] = useState(stepProp || DocumentDialogStates.INITIAL)
  const [iframeDocument, setIframeDocument] = useState<Document | null>(null)
  const [isLoading, setIsLoading] = useState()

  const documentRef = useRef<DocumentHandle>(null)
  const editorRef = useRef<DocumentEditorHandle>(null)

  const isImage = matchImageExtension(document?.extension)
  const formattedDate = moment(document?.creationDate).format(
    DateFormat.FULL_DATE_COMMA_PIPE_TIME,
  )
  const FormType = Utils.findConstantIdByName('Form', DocumentTypes) || {}

  const isInputFormsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.INPUT_FORMS_DOCUMENT),
  )

  useErrorAlert({
    errorSelector: getDocumentValidationError,
    onCloseAction: clearDocumentValidationError(),
    onOpenAction: soapId ? fetchSoap(soapId, soapBusinessId) : null,
  })

  const onIframeLoad = (event: IFrameEvent) => {
    const newDocument = event?.target?.contentDocument
    setIframeDocument(newDocument)
  }

  const onPreviewBackRequested = () => {
    setStep(DocumentDialogStates.EDITOR)
  }

  const doClose = () => {
    setStep(stepProp || DocumentDialogStates.INITIAL)
    setDocumentCandidate(document as DocumentType)
    setIframeDocument(null)
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    if (step === DocumentDialogStates.PREVIEW && !previewOnly) {
      onPreviewBackRequested()
    } else {
      doClose()
    }
  }

  const onDocumentCreated = () => {
    doClose()
    if (lastCreatedDocumentId) {
      navigate(`/admin/catalog/documents/${lastCreatedDocumentId}`)
    }
  }

  const setCloseAfterCreationOn = useCloseAfterCreation(
    onDocumentCreated,
    getDocumentsIsSending,
  )
  const setCloseAfterUpdateOn = useCloseAfterCreation(
    doClose,
    getIsAttachingDocuments,
  )

  useEffect(() => {
    setDocumentCandidate(document as DocumentType)
  }, [document])

  useEffect(() => {
    setStep(stepProp || DocumentDialogStates.INITIAL)
  }, [stepProp])

  const onDocumentUpdate = (file: FileTemplate) => {
    if (!disableCloseAfterUpdate) {
      setCloseAfterUpdateOn()
    }
    if (soapId) {
      dispatch(updateDocumentOnSoap(soapId, soapBusinessId, file as SoapFile))
    }
  }

  const onDocumentCandidateReady = (newDocument: DocumentType) => {
    const isForm = newDocument.type === FormType
    if (isInputFormsEnabled && isForm) {
      navigate(`/admin/catalog/documents/new${location.search}`, {
        state: {
          newDocument,
        },
      })
      doClose()
    } else {
      setDocumentCandidate(newDocument)
      setStep(DocumentDialogStates.EDITOR)
      navigate(addSearch(location, '/admin/catalog/documents'))
    }
  }

  const onEditorProceedRequested = () => {
    const newDocument = editorRef.current?.get()
    setDocumentCandidate(newDocument)
    setCloseAfterCreationOn()
    dispatch(createDocument(newDocument))
  }

  const onEditorPreviewRequested = () => {
    const newDocument = editorRef.current?.get()
    setDocumentCandidate(newDocument)
    setStep(DocumentDialogStates.PREVIEW)
  }

  const onEditorBackRequested = () => {
    setStep(DocumentDialogStates.INITIAL)
  }

  const onEditorFileSelected = (file: FileTemplate) => {
    const template = createTemplateFromFile(file)

    setDocumentCandidate({ ...documentCandidate, template })
    setStep(DocumentDialogStates.PREVIEW)
  }

  const onPreviewProceedRequested = (newDocument: DocumentType) => {
    setDocumentCandidate(newDocument)
    setCloseAfterCreationOn()
    dispatch(createDocument(newDocument))
  }

  const onPreviewFileSelected = (file: FileTemplate) => {
    const template = createTemplateFromFile(file)

    setDocumentCandidate({ ...documentCandidate, template })
  }

  const onDocumentSigned = (newDocumentCandidate: DocumentType | Nil) => {
    if (newDocumentCandidate) {
      setDocumentCandidate(newDocumentCandidate)
    }
  }

  const getHasUnsavedChangesForStep = R.cond([
    [R.equals(DocumentDialogStates.INITIAL), safeGetHasChanges(documentRef)],
    [R.equals(DocumentDialogStates.PREVIEW), R.F],
    [R.equals(DocumentDialogStates.EDITOR), safeGetHasChanges(editorRef)],
    [R.T, R.F],
  ])

  const getHasUnsavedChanges = () => getHasUnsavedChangesForStep(step)

  return (
    <PuiDialog
      confirmSaveOnClose
      ConfirmCloseDialogProps={{
        title: t('Dialogs:DOCUMENT_DIALOG.CONFIRM_CLOSE_DIALOG_TITLE'),
        okLabel: t('Common:NO'),
        notOkLabel: t('Common:YES'),
        onOk: R.F,
      }}
      actions={
        <>
          {step === DocumentDialogStates.PREVIEW && (
            <DocumentPreviewActions
              conversationId={conversationId}
              document={documentCandidate}
              iframeDocument={iframeDocument}
              showSaveButton={!isImage}
              view={view}
              onBack={onPreviewBackRequested}
              onDocumentSigned={onDocumentSigned}
              onDocumentUpdate={onDocumentUpdate}
              onFileSelected={onPreviewFileSelected}
              onProceed={onPreviewProceedRequested}
              {...PreviewProps}
            />
          )}
          {step === DocumentDialogStates.EDITOR && (
            <DocumentEditorActions
              document={documentCandidate}
              onBack={onEditorBackRequested}
              onFileSelected={onEditorFileSelected}
              onPreview={onEditorPreviewRequested}
              onProceed={onEditorProceedRequested}
            />
          )}
        </>
      }
      aria-labelledby="document-dialog"
      classes={{
        paper: classes.paper,
      }}
      disableClose={isLoading}
      hasUnsavedChanges={getHasUnsavedChanges}
      open={open}
      scroll="paper"
      title={
        <>
          {step === DocumentDialogStates.INITIAL &&
            t('Dialogs:DOCUMENT_DIALOG.NEW_DOCUMENT')}
          {step === DocumentDialogStates.EDITOR &&
            t('Dialogs:DOCUMENT_DIALOG.CREATE_YOUR_DOCUMENT')}
          {step === DocumentDialogStates.PREVIEW && t('Common:PREVIEW_NOUN')}
        </>
      }
      onClose={handleClose}
    >
      {step === DocumentDialogStates.INITIAL && (
        <Document
          document={documentCandidate}
          ref={documentRef}
          onProceed={onDocumentCandidateReadyProps || onDocumentCandidateReady}
        />
      )}
      {step === DocumentDialogStates.EDITOR && (
        <DocumentEditor document={documentCandidate} ref={editorRef} />
      )}
      {step === DocumentDialogStates.PREVIEW && (
        <>
          <DocumentPreview
            fullScreen
            hideDownloadButton
            document={documentCandidate}
            setIsLoading={setIsLoading}
            view={view}
            onFileSelected={onPreviewFileSelected}
            onIframeLoad={onIframeLoad}
            {...PreviewProps}
          />
          <Grid container alignItems="flex-end" direction="column" pr={2}>
            {isImage && document?.author && (
              <Text inline variant="lowAccent2">
                {t('Common:ADDED_BY_PERSON_NAME', {
                  personName: Utils.getPersonString(document.author),
                })}
              </Text>
            )}
            {isImage && document?.creationDate && (
              <Text inline variant="lowAccent2">
                {formattedDate}
              </Text>
            )}
          </Grid>
        </>
      )}
    </PuiDialog>
  )
}

export default DocumentDialog
